import { useCallback, useEffect, useRef } from 'react';

import { useInviteLeaseDetailsDrawerStore } from '@/modules/application-tracker/stores';
import { InviteLeaseDetailsSegmentEvents } from '../constants';
import type { CloseReason, InviteFlowMilestoneKey } from '../types';
import { shouldEmitFromUrl } from '../utils';
import { useInviteTracking } from './use-invite-tracking';

const createEmptyMilestones = (): Record<InviteFlowMilestoneKey, boolean> => ({
  hadFlowStarted: false,
  hadPropertySelected: false,
  hadLabelSelected: false,
  hadSubmitAttempt: false,
  hadSubmitSuccess: false,
});

export const useInviteDrawerSessionTracking = () => {
  const { isOpen, flowId, fromUrl } = useInviteLeaseDetailsDrawerStore();
  const { trackInvite, entryPoint } = useInviteTracking();

  const milestonesRef = useRef<Record<InviteFlowMilestoneKey, boolean>>(createEmptyMilestones());
  const prevOpenRef = useRef(isOpen);

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      const includeFromUrl = shouldEmitFromUrl(entryPoint, fromUrl);
      trackInvite(InviteLeaseDetailsSegmentEvents.DrawerOpened, {
        entry_point: entryPoint,
        ...(includeFromUrl ? { from_url: fromUrl } : {}),
      });
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, trackInvite, entryPoint, fromUrl]);

  useEffect(() => {
    if (flowId) {
      milestonesRef.current = createEmptyMilestones();
    }
  }, [flowId]);

  const recordInviteFlowMilestone = useCallback((key: InviteFlowMilestoneKey) => {
    milestonesRef.current[key] = true;
  }, []);

  // Must fire exactly once per session; caller owns the guard (see RenterInvite.drawer.tsx).
  const trackDrawerClosed = useCallback(
    (reason: CloseReason) => {
      trackInvite(InviteLeaseDetailsSegmentEvents.DrawerClosed, {
        close_reason: reason,
        had_flow_started: milestonesRef.current.hadFlowStarted,
        had_property_selected: milestonesRef.current.hadPropertySelected,
        had_label_selected: milestonesRef.current.hadLabelSelected,
        had_submit_attempt: milestonesRef.current.hadSubmitAttempt,
        had_submit_success: milestonesRef.current.hadSubmitSuccess,
      });
    },
    [trackInvite],
  );

  return { recordInviteFlowMilestone, trackDrawerClosed };
};
